// src/pages/ProjectLayout.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

const getCategories = (p) => {
  if (!p) return [];
  if (Array.isArray(p.categories)) return p.categories;
  if (Array.isArray(p.category)) return p.category;
  if (typeof p.category === "string") return [p.category];
  return [];
};

function HeroCarousel({ title, gallery = [] }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const count = gallery.length;
  const goTo = useCallback(
    (i) => setIndex(((i % count) + count) % count),
    [count]
  );
  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + count) % count),
    [count]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const dx = touchEndX.current - touchStartX.current;
    if (Math.abs(dx) > 40) dx < 0 ? next() : prev();
  };

  if (count === 0) return null;

  return (
    <section
      className="relative mb-6"
      aria-roledescription="carousel"
      aria-label={`${title} gallery`}
    >
      <div
        className="relative overflow-hidden rounded-2xl shadow"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {gallery.map(({ src, alt }, i) => (
            <div
              key={i}
              className="min-w-full select-none"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}`}
            >
              <div className="w-full aspect-[16/9] bg-gray-100 dark:bg-gray-800">
                <img
                  src={src}
                  alt={alt || `${title} image ${i + 1}`}
                  className="w-full h-full object-contain"
                  draggable="false"
                  loading={i === index ? "eager" : "lazy"}
                />
              </div>
            </div>
          ))}
        </div>

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 dark:bg-black/40 backdrop-blur px-4 py-3 shadow hover:bg-white dark:hover:bg-black focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 dark:bg-black/40 backdrop-blur px-4 py-3 shadow hover:bg-white dark:hover:bg-black focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              ›
            </button>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          {gallery.map((_, i) => {
            const active = i === index;
            return (
              <button
                key={i}
                type="button"
                aria-label={`Go to image ${i + 1}`}
                aria-current={active ? "true" : "false"}
                onClick={() => goTo(i)}
                className={[
                  "h-2.5 rounded-full transition-all",
                  active
                    ? "w-6 bg-blue-600"
                    : "w-2.5 bg-gray-300 dark:bg-gray-600",
                ].join(" ")}
              />
            );
          })}
        </div>
      )}

      {count > 1 && (
        <div className="mt-4">
          <div className="flex gap-2 overflow-x-auto lg:grid lg:grid-cols-8 lg:overflow-visible no-scrollbar">
            {gallery.map(({ src, alt }, i) => {
              const active = i === index;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  className={[
                    "relative rounded-lg overflow-hidden border shrink-0 w-24 h-16 lg:w-auto lg:h-auto",
                    active
                      ? "border-blue-600 ring-2 ring-blue-600"
                      : "border-transparent hover:border-gray-300 dark:hover:border-gray-600",
                  ].join(" ")}
                  aria-label={`Preview image ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={alt || `${title} thumbnail ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default function ProjectLayout({ project, gallery = [], children }) {
  const navigate = useNavigate(); // ✅ hook used inside component

  if (!project) {
    return (
      <div className="relative bg-white dark:bg-gray-900 transition-colors duration-300 min-h-[calc(100vh-4rem)]">
        <button
          type="button"
          onClick={() => navigate("/projects")}
          className="absolute top-4 right-4 rounded-md bg-blue-600 px-4 py-2 text-white"
        >
          Back to Projects
        </button>
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Project not found
          </h1>
        </div>
      </div>
    );
  }

  const categories = getCategories(project);

  return (
    <div className="relative bg-white dark:bg-gray-900 transition-colors duration-300 min-h-[calc(100vh-4rem)]">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto mb-6">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            ← Back to Projects
          </Link>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Ensure the project's cover image is included first in the gallery */}
          {(() => {
            const normalized = Array.isArray(gallery) ? [...gallery] : [];
            // convert string entries to objects
            const normObjs = normalized.map((g) => (typeof g === "string" ? { src: g } : g));
            const cover = project.imageUrl;
            const hasCover = normObjs.some((g) => g && (g.src || g).toString() === (cover || "").toString());
            const finalGallery = cover && !hasCover ? [{ src: cover, alt: project.title }, ...normObjs] : normObjs;
            return <HeroCarousel title={project.title} gallery={finalGallery} />;
          })()}
        </div>

        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            {project.title}
          </h1>

          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((c) => (
              <span
                key={c}
                className="px-3 py-1 rounded-full text-sm bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
              >
                {c}
              </span>
            ))}
            {project.status && (
              <span className="px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-200">
                {project.status}
              </span>
            )}
          </div>

          {project.tags?.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Tags
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {project.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md text-sm text-gray-800 dark:text-gray-100"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="prose dark:prose-invert max-w-none">
            {project.description && (
              <p className="text-lg text-gray-600 dark:text-gray-200">
                {project.description}
              </p>
            )}

            {children}

            {project.details &&
              typeof project.details === "string" &&
              project.details.trim() !== "" &&
              project.details !== "Detailed information about the project TBD soon..." && (
                <div className="mt-6 whitespace-pre-line text-gray-700 dark:text-gray-300">
                  {project.details}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
